import React, { useState, useCallback } from "react";
import { Search, ArrowRight, ArrowLeft, Package, Factory, ShoppingCart, Truck, FileText, Download, ChevronRight } from "lucide-react";
import api from "../utils/api";
import { toast } from "sonner";

function TraceCard({ icon: Icon, color, title, children }) {
  return (
    <div className={`border rounded-xl p-4 ${color}`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} />
        <h3 className="font-semibold text-sm">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function ForwardResult({ data }) {
  if (!data) return null;
  const { receiving, batches, sales } = data;
  return (
    <div className="space-y-3" data-testid="forward-result">
      <TraceCard icon={Package} color="border-blue-200 bg-blue-50/50" title="Recepcion de Materia Prima">
        <div className="text-xs space-y-1">
          <div className="flex justify-between"><span className="text-slate-500">Folio:</span><span className="font-medium">{receiving?.folio || "N/A"}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Proveedor:</span><span className="font-medium">{receiving?.supplier_name || "N/A"}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Lote proveedor:</span><span className="font-medium">{receiving?.supplier_lot || "N/A"}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Producto:</span><span className="font-medium">{receiving?.product_name || "N/A"}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Cant. comprada:</span><span className="font-medium">{receiving?.purchased_qty || 0} kg</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Cant. real:</span><span className="font-medium">{receiving?.real_qty || 0} kg</span></div>
        </div>
      </TraceCard>
      <div className="flex justify-center"><ChevronRight size={20} className="text-slate-300 rotate-90" /></div>
      <TraceCard icon={Factory} color="border-amber-200 bg-amber-50/50" title={`Lotes de Produccion (${batches.length})`}>
        {batches.length === 0 ? <p className="text-xs text-slate-400 italic">Sin lotes vinculados</p> : (
          <div className="space-y-2">
            {batches.map(b => (
              <div key={b.id} className="bg-white rounded-lg p-2 border border-amber-100 text-xs">
                <div className="flex justify-between items-center">
                  <span className="font-mono font-bold text-amber-800">{b.batch_number}</span>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                    b.status === "completado" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
                  }`}>{b.status}</span>
                </div>
                <div className="text-slate-500 mt-0.5">{b.product_name} | Entrada: {b.raw_kg}kg | Salida: {b.actual_output_kg || "?"}kg</div>
              </div>
            ))}
          </div>
        )}
      </TraceCard>
      <div className="flex justify-center"><ChevronRight size={20} className="text-slate-300 rotate-90" /></div>
      <TraceCard icon={ShoppingCart} color="border-green-200 bg-green-50/50" title={`Ventas / Pedidos (${sales.length})`}>
        {sales.length === 0 ? <p className="text-xs text-slate-400 italic">Sin ventas vinculadas</p> : (
          <div className="space-y-1">
            {sales.map(s => (
              <div key={s.id} className="bg-white rounded-lg p-2 border border-green-100 text-xs flex justify-between">
                <span className="font-mono font-medium">{s.folio}</span>
                <span>{s.customer_name || "Sin cliente"}</span>
                <span className="font-medium">${(s.total || 0).toFixed(2)}</span>
                <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                  s.status === "pagado" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"
                }`}>{s.status}</span>
              </div>
            ))}
          </div>
        )}
      </TraceCard>
    </div>
  );
}

function BackwardResult({ data }) {
  if (!data) return null;
  const { sale, batches, receivings } = data;
  return (
    <div className="space-y-3" data-testid="backward-result">
      <TraceCard icon={ShoppingCart} color="border-green-200 bg-green-50/50" title="Venta / Pedido">
        <div className="text-xs space-y-1">
          <div className="flex justify-between"><span className="text-slate-500">Folio:</span><span className="font-medium">{sale?.folio || "N/A"}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Cliente:</span><span className="font-medium">{sale?.customer_name || "Sin cliente"}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Total:</span><span className="font-medium">${(sale?.total || 0).toFixed(2)}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Estatus:</span><span className="font-medium">{sale?.status}</span></div>
          {sale?.items?.length > 0 && (
            <div className="mt-2 border-t border-green-100 pt-1">
              <span className="text-[10px] text-slate-400 uppercase">Productos:</span>
              {sale.items.map((it, i) => (
                <div key={i} className="flex justify-between text-slate-600">
                  <span>{it.product_name}</span>
                  <span>{it.quantity} {it.unit} x ${it.unit_price}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </TraceCard>
      <div className="flex justify-center"><ChevronRight size={20} className="text-slate-300 -rotate-90" /></div>
      <TraceCard icon={Factory} color="border-amber-200 bg-amber-50/50" title={`Lotes de Produccion (${batches.length})`}>
        {batches.length === 0 ? <p className="text-xs text-slate-400 italic">Sin lotes vinculados</p> : (
          <div className="space-y-2">
            {batches.map(b => (
              <div key={b.id} className="bg-white rounded-lg p-2 border border-amber-100 text-xs">
                <div className="font-mono font-bold text-amber-800">{b.batch_number}</div>
                <div className="text-slate-500">{b.product_name} | {b.status}</div>
                {(b.receiving_lots || []).length > 0 && (
                  <div className="mt-1 text-[10px] text-slate-400">
                    MP usada: {b.receiving_lots.map(rl => `${rl.supplier_name || ""} (${rl.qty || "?"}kg)`).join(", ")}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </TraceCard>
      <div className="flex justify-center"><ChevronRight size={20} className="text-slate-300 -rotate-90" /></div>
      <TraceCard icon={Truck} color="border-blue-200 bg-blue-50/50" title={`Recepciones de Materia Prima (${receivings.length})`}>
        {receivings.length === 0 ? <p className="text-xs text-slate-400 italic">Sin recepciones vinculadas</p> : (
          <div className="space-y-1">
            {receivings.map(r => (
              <div key={r.id} className="bg-white rounded-lg p-2 border border-blue-100 text-xs">
                <div className="flex justify-between">
                  <span className="font-mono font-medium">{r.folio}</span>
                  <span className="font-medium">{r.supplier_name}</span>
                </div>
                <div className="text-slate-500">
                  {r.product_name} | Lote: {r.supplier_lot || "N/A"} | {r.purchased_qty || 0}kg comprado | {r.real_qty || 0}kg real
                </div>
              </div>
            ))}
          </div>
        )}
      </TraceCard>
    </div>
  );
}

export default function Trazabilidad() {
  const [mode, setMode] = useState("search"); // search, forward, backward
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [traceData, setTraceData] = useState(null);
  const [loading, setLoading] = useState(false);

  const doSearch = useCallback(async (q) => {
    if (!q || q.length < 2) { setSearchResults(null); return; }
    try {
      const r = await api.get(`/traceability/search?q=${encodeURIComponent(q)}`);
      setSearchResults(r.data);
    } catch { setSearchResults(null); }
  }, []);

  const traceForward = async (receivingId) => {
    setLoading(true);
    try {
      const r = await api.get(`/traceability/forward/${receivingId}`);
      setTraceData(r.data); setMode("forward");
    } catch { toast.error("Error al rastrear"); }
    finally { setLoading(false); }
  };

  const traceBackward = async (saleId) => {
    setLoading(true);
    try {
      const r = await api.get(`/traceability/backward/${saleId}`);
      setTraceData(r.data); setMode("backward");
    } catch { toast.error("Error al rastrear"); }
    finally { setLoading(false); }
  };

  const traceBatch = async (batchId) => {
    setLoading(true);
    try {
      const r = await api.get(`/traceability/by-batch/${batchId}`);
      setTraceData(r.data); setMode("batch");
    } catch { toast.error("Error al rastrear"); }
    finally { setLoading(false); }
  };

  const exportPDF = () => {
    const w = window.open("", "_blank");
    let html = `<html><head><title>Reporte de Trazabilidad</title>
      <style>body{font-family:Arial,sans-serif;padding:20px;font-size:12px}h1{font-size:18px;border-bottom:2px solid #333;padding-bottom:8px}
      h2{font-size:14px;margin-top:16px;color:#333}.card{border:1px solid #ccc;border-radius:8px;padding:12px;margin:8px 0}
      table{width:100%;border-collapse:collapse;margin:8px 0}th,td{border:1px solid #ddd;padding:6px;text-align:left}
      th{background:#f5f5f5}.meta{color:#666;font-size:11px}.footer{margin-top:30px;border-top:1px solid #ccc;padding-top:10px;font-size:10px;color:#999}
      @media print{body{padding:10px}}</style></head><body>`;
    html += `<h1>Reporte de Trazabilidad - SENASICA</h1>`;
    html += `<p class="meta">Generado: ${new Date().toLocaleString("es-MX")} | Empresa: Snacks Deshidratados</p>`;

    if (mode === "forward" && traceData) {
      const { receiving, batches, sales } = traceData;
      html += `<h2>Trazabilidad Hacia Adelante</h2>`;
      html += `<div class="card"><strong>Recepcion:</strong> ${receiving?.folio || "N/A"} | Proveedor: ${receiving?.supplier_name || "N/A"} | Lote: ${receiving?.supplier_lot || "N/A"} | Producto: ${receiving?.product_name || ""} | Cant: ${receiving?.purchased_qty || 0}kg</div>`;
      if (batches.length > 0) {
        html += `<h2>Lotes de Produccion</h2><table><tr><th>Lote</th><th>Producto</th><th>Entrada</th><th>Salida</th><th>Estado</th><th>Fecha</th></tr>`;
        batches.forEach(b => { html += `<tr><td>${b.batch_number}</td><td>${b.product_name}</td><td>${b.raw_kg}kg</td><td>${b.actual_output_kg || "?"}kg</td><td>${b.status}</td><td>${(b.created_at || "").slice(0, 10)}</td></tr>`; });
        html += `</table>`;
      }
      if (sales.length > 0) {
        html += `<h2>Ventas / Pedidos</h2><table><tr><th>Folio</th><th>Cliente</th><th>Total</th><th>Estado</th></tr>`;
        sales.forEach(s => { html += `<tr><td>${s.folio}</td><td>${s.customer_name || ""}</td><td>$${(s.total || 0).toFixed(2)}</td><td>${s.status}</td></tr>`; });
        html += `</table>`;
      }
    } else if (mode === "backward" && traceData) {
      const { sale, batches, receivings } = traceData;
      html += `<h2>Trazabilidad Hacia Atras</h2>`;
      html += `<div class="card"><strong>Venta:</strong> ${sale?.folio || "N/A"} | Cliente: ${sale?.customer_name || "N/A"} | Total: $${(sale?.total || 0).toFixed(2)} | Estado: ${sale?.status}</div>`;
      if (sale?.items?.length > 0) {
        html += `<table><tr><th>Producto</th><th>Cantidad</th><th>Precio</th></tr>`;
        sale.items.forEach(i => { html += `<tr><td>${i.product_name}</td><td>${i.quantity} ${i.unit}</td><td>$${i.unit_price}</td></tr>`; });
        html += `</table>`;
      }
      if (batches.length > 0) {
        html += `<h2>Lotes de Produccion</h2><table><tr><th>Lote</th><th>Producto</th><th>Estado</th><th>MP Usada</th></tr>`;
        batches.forEach(b => {
          const mp = (b.receiving_lots || []).map(rl => `${rl.supplier_name || ""} ${rl.qty || "?"}kg`).join(", ") || "N/A";
          html += `<tr><td>${b.batch_number}</td><td>${b.product_name}</td><td>${b.status}</td><td>${mp}</td></tr>`;
        });
        html += `</table>`;
      }
      if (receivings.length > 0) {
        html += `<h2>Recepciones de Materia Prima</h2><table><tr><th>Folio</th><th>Proveedor</th><th>Lote</th><th>Producto</th><th>Cantidad</th></tr>`;
        receivings.forEach(r => { html += `<tr><td>${r.folio}</td><td>${r.supplier_name}</td><td>${r.supplier_lot || "N/A"}</td><td>${r.product_name}</td><td>${r.purchased_qty || 0}kg</td></tr>`; });
        html += `</table>`;
      }
    } else if (mode === "batch" && traceData) {
      const { batch, receivings, sales, cleaning_logs } = traceData;
      html += `<h2>Trazabilidad de Lote: ${batch?.batch_number || "N/A"}</h2>`;
      html += `<div class="card"><strong>Producto:</strong> ${batch?.product_name} | Entrada: ${batch?.raw_kg}kg | Salida: ${batch?.actual_output_kg || "?"}kg | Estado: ${batch?.status}</div>`;
      if (receivings.length > 0) {
        html += `<h2>Materia Prima</h2><table><tr><th>Folio</th><th>Proveedor</th><th>Lote</th><th>Producto</th></tr>`;
        receivings.forEach(r => { html += `<tr><td>${r.folio || "N/A"}</td><td>${r.supplier_name || ""}</td><td>${r.supplier_lot || "N/A"}</td><td>${r.product_name || ""}</td></tr>`; });
        html += `</table>`;
      }
      if (sales.length > 0) {
        html += `<h2>Ventas</h2><table><tr><th>Folio</th><th>Cliente</th><th>Total</th><th>Estado</th></tr>`;
        sales.forEach(s => { html += `<tr><td>${s.folio}</td><td>${s.customer_name || ""}</td><td>$${(s.total || 0).toFixed(2)}</td><td>${s.status}</td></tr>`; });
        html += `</table>`;
      }
      if (cleaning_logs?.length > 0) {
        html += `<h2>Registros de Limpieza (periodo del lote)</h2><table><tr><th>Folio</th><th>Fecha</th><th>Area</th><th>Producto</th><th>Responsable</th></tr>`;
        cleaning_logs.forEach(c => { html += `<tr><td>${c.folio}</td><td>${c.date} ${c.time}</td><td>${c.area}</td><td>${c.cleaning_product}</td><td>${c.responsible}</td></tr>`; });
        html += `</table>`;
      }
    }
    html += `<div class="footer">Documento generado automaticamente por RasputinERP - Control de Trazabilidad SENASICA<br/>Este documento es para uso interno y auditorias regulatorias.</div>`;
    html += `</body></html>`;
    w.document.write(html);
    w.document.close();
    setTimeout(() => w.print(), 500);
  };

  return (
    <div className="space-y-4" data-testid="trazabilidad-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Trazabilidad</h1>
          <p className="text-xs text-slate-500">Rastreo completo: Compra → Produccion → Venta (SENASICA)</p>
        </div>
        {traceData && (
          <button onClick={exportPDF}
            className="flex items-center gap-1.5 bg-slate-800 hover:bg-slate-900 text-white px-4 py-2 rounded-lg text-sm font-medium"
            data-testid="export-pdf-btn">
            <Download size={14} /> Imprimir / PDF
          </button>
        )}
      </div>

      {/* Search Bar */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={query} onChange={e => { setQuery(e.target.value); doSearch(e.target.value); }}
            placeholder="Buscar por # lote, folio, proveedor, producto o cliente..."
            className="w-full pl-10 pr-3 py-2.5 border border-slate-200 rounded-lg text-sm" data-testid="trace-search" />
        </div>

        {/* Search Results */}
        {searchResults && (
          <div className="mt-3 space-y-3" data-testid="trace-search-results">
            {searchResults.receivings?.length > 0 && (
              <div>
                <h4 className="text-[10px] font-bold text-slate-400 uppercase mb-1">Recepciones de MP</h4>
                {searchResults.receivings.map(r => (
                  <button key={r.id} onClick={() => traceForward(r.id)}
                    className="w-full text-left flex items-center justify-between p-2 hover:bg-blue-50 rounded-lg text-xs border border-transparent hover:border-blue-200 transition"
                    data-testid={`trace-recv-${r.id}`}>
                    <div className="flex items-center gap-2">
                      <Package size={12} className="text-blue-500" />
                      <span className="font-mono">{r.folio}</span>
                      <span className="text-slate-500">{r.supplier_name} | {r.product_name}</span>
                    </div>
                    <div className="flex items-center gap-1 text-blue-500"><span>Rastrear</span><ArrowRight size={12} /></div>
                  </button>
                ))}
              </div>
            )}
            {searchResults.batches?.length > 0 && (
              <div>
                <h4 className="text-[10px] font-bold text-slate-400 uppercase mb-1">Lotes de Produccion</h4>
                {searchResults.batches.map(b => (
                  <button key={b.id} onClick={() => traceBatch(b.id)}
                    className="w-full text-left flex items-center justify-between p-2 hover:bg-amber-50 rounded-lg text-xs border border-transparent hover:border-amber-200 transition"
                    data-testid={`trace-batch-${b.id}`}>
                    <div className="flex items-center gap-2">
                      <Factory size={12} className="text-amber-500" />
                      <span className="font-mono font-bold">{b.batch_number}</span>
                      <span className="text-slate-500">{b.product_name}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] ${b.status === "completado" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{b.status}</span>
                    </div>
                    <div className="flex items-center gap-1 text-amber-500"><span>Ver cadena</span><FileText size={12} /></div>
                  </button>
                ))}
              </div>
            )}
            {searchResults.sales?.length > 0 && (
              <div>
                <h4 className="text-[10px] font-bold text-slate-400 uppercase mb-1">Ventas / Pedidos</h4>
                {searchResults.sales.map(s => (
                  <button key={s.id} onClick={() => traceBackward(s.id)}
                    className="w-full text-left flex items-center justify-between p-2 hover:bg-green-50 rounded-lg text-xs border border-transparent hover:border-green-200 transition"
                    data-testid={`trace-sale-${s.id}`}>
                    <div className="flex items-center gap-2">
                      <ShoppingCart size={12} className="text-green-500" />
                      <span className="font-mono font-medium">{s.folio}</span>
                      <span className="text-slate-500">{s.customer_name}</span>
                      <span className="font-medium">${(s.total || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex items-center gap-1 text-green-500"><ArrowLeft size={12} /><span>Rastrear origen</span></div>
                  </button>
                ))}
              </div>
            )}
            {!searchResults.receivings?.length && !searchResults.batches?.length && !searchResults.sales?.length && (
              <p className="text-xs text-slate-400 text-center py-4">Sin resultados para "{query}"</p>
            )}
          </div>
        )}
      </div>

      {/* Trace Results */}
      {loading && <div className="text-center py-8 text-slate-400">Rastreando...</div>}

      {!loading && traceData && (
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-sm text-slate-800">
              {mode === "forward" && "Trazabilidad Hacia Adelante (Recepcion → Venta)"}
              {mode === "backward" && "Trazabilidad Hacia Atras (Venta → Recepcion)"}
              {mode === "batch" && `Cadena del Lote: ${traceData.batch?.batch_number || ""}`}
            </h3>
            <button onClick={() => { setTraceData(null); setMode("search"); }}
              className="text-xs text-slate-500 hover:text-slate-700">Limpiar</button>
          </div>
          {mode === "forward" && <ForwardResult data={traceData} />}
          {mode === "backward" && <BackwardResult data={traceData} />}
          {mode === "batch" && (
            <div className="space-y-3" data-testid="batch-trace-result">
              <TraceCard icon={Factory} color="border-amber-200 bg-amber-50/50" title="Lote de Produccion">
                <div className="text-xs space-y-1">
                  <div className="flex justify-between"><span className="text-slate-500">Numero:</span><span className="font-mono font-bold">{traceData.batch?.batch_number}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Producto:</span><span className="font-medium">{traceData.batch?.product_name}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Entrada:</span><span>{traceData.batch?.raw_kg} kg</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Salida:</span><span>{traceData.batch?.actual_output_kg || "?"} kg</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Estado:</span><span>{traceData.batch?.status}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Creado:</span><span>{(traceData.batch?.created_at || "").slice(0, 10)}</span></div>
                  {traceData.batch?.completed_at && <div className="flex justify-between"><span className="text-slate-500">Completado:</span><span>{traceData.batch.completed_at.slice(0, 10)}</span></div>}
                </div>
              </TraceCard>
              {traceData.receivings?.length > 0 && (
                <>
                  <div className="flex justify-center"><ChevronRight size={20} className="text-slate-300 -rotate-90" /></div>
                  <TraceCard icon={Package} color="border-blue-200 bg-blue-50/50" title="Materia Prima">
                    <div className="space-y-1">
                      {traceData.receivings.map(r => (
                        <div key={r.id} className="bg-white rounded-lg p-2 border border-blue-100 text-xs">
                          <span className="font-mono">{r.folio || "N/A"}</span> | {r.supplier_name} | Lote: {r.supplier_lot || "N/A"} | {r.product_name}
                        </div>
                      ))}
                    </div>
                  </TraceCard>
                </>
              )}
              {traceData.sales?.length > 0 && (
                <>
                  <div className="flex justify-center"><ChevronRight size={20} className="text-slate-300 rotate-90" /></div>
                  <TraceCard icon={ShoppingCart} color="border-green-200 bg-green-50/50" title="Ventas Vinculadas">
                    <div className="space-y-1">
                      {traceData.sales.map(s => (
                        <div key={s.id} className="bg-white rounded-lg p-2 border border-green-100 text-xs flex justify-between">
                          <span className="font-mono">{s.folio}</span>
                          <span>{s.customer_name || "Sin cliente"}</span>
                          <span className="font-medium">${(s.total || 0).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </TraceCard>
                </>
              )}
              {traceData.cleaning_logs?.length > 0 && (
                <>
                  <div className="flex justify-center"><ChevronRight size={20} className="text-slate-300 rotate-90" /></div>
                  <TraceCard icon={FileText} color="border-cyan-200 bg-cyan-50/50" title="Registros de Limpieza (periodo)">
                    <div className="space-y-1">
                      {traceData.cleaning_logs.map(c => (
                        <div key={c.id} className="bg-white rounded-lg p-2 border border-cyan-100 text-xs">
                          {c.folio} | {c.date} {c.time} | {c.area} | {c.cleaning_product} | {c.responsible}
                        </div>
                      ))}
                    </div>
                  </TraceCard>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Instructions when no results */}
      {!traceData && !searchResults && (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
          <FileText size={36} className="mx-auto text-slate-300 mb-3" />
          <h3 className="font-semibold text-slate-700 mb-1">Busca un lote, pedido o recepcion</h3>
          <p className="text-xs text-slate-400 max-w-md mx-auto">
            Escribe un numero de lote (ej: PULMON-20260315-001), folio de recepcion, nombre de proveedor o folio de venta para rastrear toda la cadena.
          </p>
          <div className="mt-4 flex justify-center gap-6 text-xs text-slate-500">
            <div className="flex items-center gap-1"><ArrowRight size={12} className="text-blue-500" /> Hacia adelante: Recepcion → Produccion → Venta</div>
            <div className="flex items-center gap-1"><ArrowLeft size={12} className="text-green-500" /> Hacia atras: Venta → Produccion → Recepcion</div>
          </div>
        </div>
      )}
    </div>
  );
}
